import {isFeatureEnabled} from 'feature-flags';
import {DataStatus, TabProps, Tabs} from 'platform/components';
import {Show} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';

import {defaultTo} from 'ramda';
import {isFalse, isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetTenantQuery} from '@dms/api/core';
import {
  useGetDeliveryNoteQuery,
  useGetReceiveNoteInfoQuery,
  useGetReceiveNoteItemsTotalPriceQuery,
  useGetReceiveNoteFastTrackItemsQuery,
  useGetReceiveNoteStateQuery,
  useGetWarehouseQuery,
} from '@dms/api/warehouse';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {
  DEFAULT_CURRENCY,
  queryParams,
  refreshDmsDatagrid,
  Section,
  usePermissions,
} from '@dms/shared';
import {useWarehouseParams} from '@dms/warehouse/shared-components';

import {buildArray, RequiredTestIdProps, suffixTestId, useBoolean, useQueryState} from 'shared/web';

import {isReceiveNoteFastTracksPending} from '../../utils/isReceiveNoteFastTracksPending';
import {FastTrackIssuesTab} from './components/FastTrackIssuesTab';
import {GeneralInformationTab} from './components/GeneralInformationTab';
import {ListOfItemsTab} from './components/ListOfItemsTab';
import {ReceiveNoteMaterialModal} from './components/ReceiveNoteMaterialModal';
import {TotalsTab} from './components/TotalsTab';

interface OverviewProps extends RequiredTestIdProps {
  receiveNoteId: string;
  isReceiveNoteLoading: boolean;
  hasReceiveNoteError: boolean;
}

// eslint-disable-next-line complexity, max-statements -- Pre-existing violation
export function Overview(props: OverviewProps) {
  const {receiveNoteId} = useWarehouseParams();

  const {data: receiveNoteState} = useGetReceiveNoteStateQuery(
    {receiveNoteId},
    {skip: isNilOrEmpty(receiveNoteId)}
  );
  const {data: receiveNoteInfo} = useGetReceiveNoteInfoQuery(
    {receiveNoteId},
    {skip: isNilOrEmpty(receiveNoteId)}
  );

  const isFastTrackFeatureFlagEnabled = isFeatureEnabled(
    featureFlags.AFS_WAREHOUSE_FAST_TRACK_ISSUE
  );

  const {data: fastTrackItems} = useGetReceiveNoteFastTrackItemsQuery(
    {receiveNoteId},
    {skip: isNilOrEmpty(receiveNoteId) || isFalse(isFastTrackFeatureFlagEnabled)}
  );

  const [activeTabId, setActiveTabId] = useQueryState(queryParams.COMPONENT_SECTIONS_TAB);

  const [canEditReceiveNote] = usePermissions({
    permissionKeys: ['receiveNoteEdit'],
  });

  const [isMaterialModalVisible, openMaterialModal, closeMaterialModal] = useBoolean();

  const formatCurrency = useFormatCurrency();

  const {data: tenant, isLoading: isTenantLoading, isError: isTenantError} = useGetTenantQuery();

  const {data: totalPrice} = useGetReceiveNoteItemsTotalPriceQuery(
    {receiveNoteId},
    {skip: isNilOrEmpty(receiveNoteId)}
  );

  const {data: warehouse} = useGetWarehouseQuery(
    {warehouseId: receiveNoteInfo?.warehouseId as string},
    {skip: isNilOrEmpty(receiveNoteInfo?.warehouseId)}
  );

  const {data: deliveryNote} = useGetDeliveryNoteQuery(
    {deliveryNoteId: receiveNoteInfo?.deliveryNote?.deliveryNoteId as string},
    {skip: isNilOrEmpty(receiveNoteInfo?.deliveryNote?.deliveryNoteId)}
  );

  const isDraftReceiveNote = isNilOrEmpty(receiveNoteId);
  const isFromReceiveNote = defaultTo(false, receiveNoteInfo?.deliveryNote.isFromReceiveNote);

  const defaultCurrency = tenant?.currency ?? DEFAULT_CURRENCY;

  const isNewReceiveNoteDetailDataLoaded = !tenant;

  const isEditReceiveNoteDetailDataLoaded = isNewReceiveNoteDetailDataLoaded || !receiveNoteState;

  const isNewReceiveNoteDetailDataLoading = isTenantLoading || props.isReceiveNoteLoading;

  const isEditReceiveNoteDetailDataLoading =
    isNewReceiveNoteDetailDataLoading || props.isReceiveNoteLoading;

  const isNewReceiveNoteDetailDataError = isTenantError || props.hasReceiveNoteError;

  const isEditReceiveNoteDetailDataError =
    isNewReceiveNoteDetailDataError || props.hasReceiveNoteError;

  if (receiveNoteId ? isEditReceiveNoteDetailDataLoaded : isNewReceiveNoteDetailDataLoaded) {
    return (
      <DataStatus
        isLoading={
          receiveNoteId ? isEditReceiveNoteDetailDataLoading : isNewReceiveNoteDetailDataLoading
        }
        isError={receiveNoteId ? isEditReceiveNoteDetailDataError : isNewReceiveNoteDetailDataError}
        minHeight="100vh"
        data-testid={suffixTestId('status', props)}
      />
    );
  }

  const handleDiscardMaterialModal = () => {
    closeMaterialModal();
    refreshDmsDatagrid('receive-note-item');
  };

  const isReceiveNotePending = receiveNoteState?.state === 'PENDING';

  const isReceiveNoteCompleted = receiveNoteState?.state === 'COMPLETED';

  const totalPriceWithoutVat = formatCurrency(
    totalPrice?.totalPriceWithoutVat ?? 0,
    warehouse?.currency ?? defaultCurrency,
    2
  );

  const totalPriceWithVat = formatCurrency(
    totalPrice?.totalPriceWithVat ?? 0,
    warehouse?.currency ?? defaultCurrency,
    2
  );

  const isPending = isReceiveNoteFastTracksPending(fastTrackItems);

  const deliveryNotePriceWithoutVat = deliveryNote?.totalPrice?.withoutVat?.amount ?? 0;
  const deliveryNotePriceWithVat = deliveryNote?.totalPrice?.withVat?.amount ?? 0;
  const receiveNotePriceWithoutVat = totalPrice?.totalPriceWithoutVat ?? 0;
  const receiveNotePriceWithVat = totalPrice?.totalPriceWithVat ?? 0;
  const checkingTotalsWithoutVat = receiveNotePriceWithoutVat - deliveryNotePriceWithoutVat;
  const checkingTotalsWithVat = receiveNotePriceWithVat - deliveryNotePriceWithVat;
  const hasTotalsDiscrepancy = checkingTotalsWithoutVat !== 0 || checkingTotalsWithVat !== 0;
  const specialConditionForEnablingAddMaterial =
    isReceiveNotePending && receiveNoteState?.type === 'PURCHASE' && !isFromReceiveNote;

  const isAddMaterialDisabledByDefaultConditions =
    isReceiveNoteCompleted || !isFromReceiveNote || !canEditReceiveNote;

  const isAddMaterialDisabled =
    !specialConditionForEnablingAddMaterial && isAddMaterialDisabledByDefaultConditions;

  const getAddMaterialTooltip = () => {
    if (!isAddMaterialDisabled) {
      return undefined;
    }

    if (isReceiveNoteCompleted) {
      return i18n.t('entity.warehouse.tooltips.addMaterialDisabledCompleted');
    }

    if (!canEditReceiveNote) {
      return i18n.t('entity.warehouse.tooltips.addMaterialDisabledNoPermission');
    }

    if (!isFromReceiveNote) {
      return i18n.t('entity.warehouse.tooltips.addMaterialDisabledNotFromReceiveNote');
    }

    return i18n.t('entity.warehouse.tooltips.addMaterialDisabled');
  };

  const tabs = buildArray<TabProps>()
    .add({
      title: i18n.t('entity.warehouse.labels.generalInformation'),
      id: 'generalInformation',
      content: (
        <GeneralInformationTab
          receiveNoteId={receiveNoteId}
          receiveNoteState={receiveNoteState}
          receiveNoteGeneralInfo={receiveNoteInfo}
          receiveNoteDeliveryNote={receiveNoteInfo}
          warehouse={warehouse}
          tenant={tenant}
          isDraftReceiveNote={isDraftReceiveNote}
          isFromReceiveNote={isFromReceiveNote}
          data-testid={suffixTestId('generalInformation', props)}
        />
      ),
    })
    .add({
      title: i18n.t('entity.warehouse.labels.listOfItems'),
      id: 'listOfItems',
      actions: [
        {
          type: 'button',
          variant: 'outlined',
          title: i18n.t('entity.warehouse.labels.addMaterial'),
          isDisabled: isAddMaterialDisabled,
          tooltip: getAddMaterialTooltip(),
          onClick: () => openMaterialModal(),
          'data-testid': suffixTestId('actions.add', props),
        },
      ],
      content: (
        <ListOfItemsTab
          receiveNoteId={receiveNoteId}
          receiveNoteState={receiveNoteState}
          receiveNoteGeneralInfo={receiveNoteInfo}
          warehouse={warehouse}
          tenant={tenant}
          isDraftReceiveNote={isDraftReceiveNote}
          isReceiveNotePending={isReceiveNotePending}
          isFromReceiveNote={isFromReceiveNote}
          totalPriceWithoutVat={totalPriceWithoutVat}
          totalPriceWithVat={totalPriceWithVat}
          data-testid={suffixTestId('listOfItems', props)}
        />
      ),
    })
    .when(isNotNilOrEmpty(fastTrackItems?.itemIds) && isFastTrackFeatureFlagEnabled, {
      title: i18n.t('entity.warehouse.labels.fastTrackIssues'),
      id: 'fastTrackIssues',
      content: (
        <FastTrackIssuesTab
          receiveNoteId={receiveNoteId}
          isReceiveNoteCompleted={isReceiveNoteCompleted}
          data-testid={suffixTestId('fastTrackIssues', props)}
        />
      ),
      icon: isPending ? 'pending' : 'success',
    })
    .add({
      title: i18n.t('entity.warehouse.labels.totals'),
      id: 'totals',
      content: (
        <TotalsTab
          deliveryNote={deliveryNote}
          receiveNoteTotalPrice={totalPrice}
          currency={warehouse?.currency}
          data-testid={suffixTestId('totals', props)}
        />
      ),
      icon: hasTotalsDiscrepancy ? 'error' : 'success',
    });

  return (
    <Section isFullHeight data-testid={testIds.warehouse.receiveNoteCreate('wrapper')}>
      <Tabs
        isFullHeight
        tabs={tabs}
        activeTabId={activeTabId}
        onChange={setActiveTabId}
        isLazy
        variant="condensed"
      />
      <Show when={isMaterialModalVisible}>
        <ReceiveNoteMaterialModal
          receiveNoteId={receiveNoteId}
          authorizationProfileId={receiveNoteInfo?.authorizationProfileId}
          receiveNoteSupplierId={receiveNoteInfo?.supplierId}
          branchId={receiveNoteInfo?.branchId}
          warehouse={warehouse}
          tenant={tenant}
          state={receiveNoteState?.state}
          onDiscard={handleDiscardMaterialModal}
          data-testid={suffixTestId('dialogs.addMaterial', props)}
        />
      </Show>
    </Section>
  );
}
